import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Divider, Group, useMantineTheme } from '@mantine/core';
import { IconPlus, IconX } from '@tabler/icons-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';

import * as C from '../../../../ui/common/CommonStyles';
import { queryClient } from '../../../../../shared/queryClient';
import { userDataStore } from '../../../../../shared/store/user.store';
import type { IRegModlinkFormValues } from '../../../../../shared/types/device';
import type { IAddModlinkParams } from '../../../../../shared/apis/device/management/types';
import useGetDeviceDetails from '../common/hooks/useGetDeviceDetails';
import { regModlinkSchema } from './utils/regModlinkSchema';
import DeviceBasicInfoForm from './ui/basicInfoForm';
import ClientInfoForm from './ui/clientInfoForm';
import { queries } from '../../../../../shared/queries';
import usePostDevice from './hooks/usePostDevice';
import usePutDevice from './hooks/usePutDevice';
import { showSuccessNotification } from '../../../../ui/notification';

export default function RegModlinkContainer() {
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const { id } = useParams<{ id: string }>();
  const { userData } = userDataStore();
  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
    reset,
  } = useForm<IRegModlinkFormValues>({
    resolver: yupResolver(regModlinkSchema),
    defaultValues: {
      mac: '',
      deviceType: null,
      serialNum: '',
      link1: null,
      link2: null,
      planId: null,
      salesManager: null,
      memo: null,
      networkType: '',
      modemId: null,
      distributorCompany: null,
    },
  });

  // 수정 시 디바이스 정보 조회
  const { deviceDetails } = useGetDeviceDetails({ id: Number(id!) });
  const { mutate: create } = usePostDevice();
  const { mutate: update } = usePutDevice();

  const onCancel = () => {
    Swal.fire({
      title: '등록을 취소하시겠습니까?',
      text: '작성 중인 내용은 저장되지 않습니다.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: theme.colors.red[5],
      confirmButtonText: '취소',
      cancelButtonText: '계속 작성',
    }).then((result: { isConfirmed: boolean }) => {
      if (result.isConfirmed) {
        navigate(`/admin/management/device`);
      }
    });
  };

  const onSubmit: SubmitHandler<IRegModlinkFormValues> = (data) => {
    if (!data.deviceType) return;
    const { model, subType } = JSON.parse(data.deviceType);
    const newData: IAddModlinkParams = {
      mac: data.mac,
      serialNum: data.serialNum,
      deviceCd: model,
      deviceSubCd: subType,
      networkType: data.networkType,
      memo: data.memo || null,
      link1: data.link1 || null,
      link2: data.link2 || null,
      planId: data.planId ? Number(data.planId) : null,
      salesManager: data.salesManager || null,
      modemId: data.modemId ? Number(data.modemId) : null,
      distributorCompany: data.distributorCompany ? Number(data.distributorCompany) : null,
    };

    if (id) {
      Swal.fire({
        title: '변경 전 확인',
        text: '이미 출고된 디바이스는 납품처 변경 시 주의가 필요합니다. 변경할 거래처가 맞는지 다시 한번 확인바랍니다. ',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: theme.colors.red[5],
        confirmButtonText: '변경',
        cancelButtonText: '취소',
      }).then((result: { isConfirmed: boolean }) => {
        if (result.isConfirmed) {
          update(
            {
              id: id,
              data: newData,
            },
            {
              onSuccess: () => {
                reset({
                  mac: '',
                  deviceType: null,
                  serialNum: '',
                  link1: null,
                  link2: null,
                  memo: '',
                  networkType: '',
                });
                queryClient.invalidateQueries(queries.devices.list._def);
                queryClient.invalidateQueries(queries.devices.details._def);
                navigate(`/admin/management/device/details/${id}?mac=${deviceDetails?.mac}`);
                showSuccessNotification('디바이스 수정 성공', '디바이스 목록으로 이동합니다.');
              },
            },
          );
        }
      });
      return;
    }
    create(newData, {
      onSuccess: (data, variables) => {
        Swal.fire({
          icon: 'success',
          title: '디바이스가 등록되었습니다. ',
          text: '추가 등록하시겠습니까?',
          showConfirmButton: true,
          showCancelButton: true,
          confirmButtonText: '계속 등록',
          cancelButtonText: '나가기',
        }).then((result: { isConfirmed: boolean }) => {
          if (result.isConfirmed) {
            reset({
              mac: '',
              deviceType: null,
              serialNum: '',
              link1: null,
              link2: null,
              planId: null,
              salesManager: '',
              modemId: null,
              distributorCompany: null,
              memo: '',
              networkType: '',
            });
          } else {
            queryClient.invalidateQueries(queries.devices.list._def);
            navigate(`/admin/management/device/details/${data.data.id}?mac=${variables.mac}`);
          }
        });
      },
    });
  };

  useEffect(() => {
    if (userData) {
      if (userData?.userRoleCd !== 'UR100002' && userData?.userRoleCd !== 'UR100001') {
        Swal.fire({
          icon: 'error',
          title: '접근 권한이 없습니다.',
          text: '디바이스 관리 페이지로 이동합니다.',
        }).then(() => {
          window.location.href = '/admin/management/device';
        });
      }
    }
  }, []);

  useEffect(() => {
    if (deviceDetails) {
      reset({
        mac: deviceDetails.mac,
        deviceType: JSON.stringify({
          model: deviceDetails.deviceCd,
          subType: deviceDetails.deviceSubCd || '',
        }),
        serialNum: deviceDetails.serialNum,
        memo: deviceDetails.memo || '',
        networkType: deviceDetails.networkType || '',
        link1: deviceDetails.link1 || null,
        link2: deviceDetails.link2 || null,
        salesManager: deviceDetails.salesManager || '',
        distributorCompany: deviceDetails.distributorCompany ? String(deviceDetails.distributorCompany) : null,
        modemId: deviceDetails.modemId ? String(deviceDetails.modemId) : null,
        planId: deviceDetails.planId ? String(deviceDetails.planId) : null,
      });
    }
  }, [deviceDetails, reset]);

  return (
    <div>
      <C.TitleBox>
        <div className="title">{`디바이스 ${id ? '수정' : '등록'}`}</div>
      </C.TitleBox>
      <C.ListBox>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DeviceBasicInfoForm {...{ register, errors, control, setValue, data: deviceDetails }} />
          <Divider my="lg" />
          <ClientInfoForm {...{ register, errors, control, data: deviceDetails }} />
          <Group justify="center" mt="xl">
            <Button
              type="submit"
              variant="filled"
              color="primary.5"
              leftSection={<IconPlus size={16} />}
              // disabled={!!deviceDetails?.shipment}
            >
              저장
            </Button>
            <Button type="button" variant="outline" onClick={onCancel} leftSection={<IconX size={16} />}>
              취소
            </Button>
          </Group>
        </form>
      </C.ListBox>
    </div>
  );
}
